/**
 * ViewerPage — live read + edit view for a viewer in a group.
 *
 * Phases, driven by SocketIO events from the group's broadcast room:
 *   1. LIVE      — recorder is capturing audio. Show live transcript +
 *                  facilitation messages. No editing.
 *   2. EDIT      — recorder ended recording (or TA ended session). Show
 *                  full transcript + editable speaker list. Viewer can
 *                  rename speakers. Recorder is the only one who can
 *                  hit Finalize, so viewer waits.
 *   3. FINALIZED — recorder finalized. Show artifacts (concept map +
 *                  7C assessment). Read-only.
 *
 * State source of truth is the session_device_id in the URL plus the
 * viewer_token in sessionStorage. The viewer page is a pure consumer —
 * it writes ONLY speaker-tag edits; all other writes come from the
 * recording device or background services.
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import io from 'socket.io-client';
import { scopedFetch } from '../services/scoped-fetch';
import FacilitationPanel from '../components/facilitation/FacilitationPanel';
import DiscussionPulsePanel from '../components/discussion-pulse';
import SevenCsPanel from '../components/seven-cs/SevenCsPanel';
import ConceptMapView from '../components/concept-map/ConceptMapView';
import TranscriptPanel from '../components/transcript-drawer/TranscriptDrawer';

const PHASE_LIVE = 'live';
const PHASE_EDIT = 'edit';
const PHASE_FINALIZED = 'finalized';

export default function ViewerPage() {
    const params = useParams();
    const navigate = useNavigate();
    const sessionDeviceId = parseInt(params.deviceId, 10);

    const [phase, setPhase] = useState(PHASE_LIVE);
    const [transcripts, setTranscripts] = useState([]);
    const [speakers, setSpeakers] = useState([]);
    const [recorderName, setRecorderName] = useState(
        sessionStorage.getItem('byod_viewer_recorder_name') || ''
    );
    const [viewerCount, setViewerCount] = useState(1);
    const [tokenValid, setTokenValid] = useState(true);
    const [editingSpeakerId, setEditingSpeakerId] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [renameError, setRenameError] = useState(null);

    const socketRef = useRef(null);

    // ---------- Guard: no token → bounce to /viewer ----------
    useEffect(() => {
        const tok = sessionStorage.getItem('byod_viewer_token');
        const storedId = sessionStorage.getItem('byod_viewer_session_device_id');
        if (!tok || !storedId || parseInt(storedId, 10) !== sessionDeviceId) {
            navigate('/viewer', { replace: true });
        }
    }, [sessionDeviceId, navigate]);

    // ---------- Hydrate phase on mount ----------
    // Viewer may be arriving fresh, reloading, or re-entering after the
    // group was already ended/finalized. One-shot SocketIO broadcasts
    // (group_ended, session_finalized) don't replay on reconnect, so we
    // have to query the current state explicitly.
    useEffect(() => {
        if (!sessionDeviceId || !tokenValid) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await scopedFetch(`/api/v1/groups/${sessionDeviceId}/state`);
                if (!res.ok || cancelled) return;
                const data = await res.json();
                if (data.phase === 'finalized') setPhase(PHASE_FINALIZED);
                else if (data.phase === 'edit') setPhase(PHASE_EDIT);
                // else leave as PHASE_LIVE (initial state)
                if (data.recorder_name && !recorderName) setRecorderName(data.recorder_name);
            } catch (e) { /* ignore; SocketIO events will catch us up */ }
        })();
        return () => { cancelled = true; };
    }, [sessionDeviceId, tokenValid]);

    // ---------- Polling + SocketIO for live transcripts + speakers ----------
    const fetchTranscripts = useCallback(async () => {
        try {
            const res = await scopedFetch(`/api/v1/devices/${sessionDeviceId}/transcripts/client`);
            if (res.status === 401 || res.status === 403) {
                setTokenValid(false);
                return;
            }
            if (!res.ok) return;
            const data = await res.json();
            if (Array.isArray(data)) {
                setTranscripts(data.sort((a, b) => (a.start_time || 0) - (b.start_time || 0)));
            }
        } catch (e) { /* ignore */ }
    }, [sessionDeviceId]);

    const fetchSpeakers = useCallback(async () => {
        try {
            const res = await scopedFetch(`/api/v1/devices/${sessionDeviceId}/speakers`);
            if (!res.ok) return;
            const data = await res.json();
            if (Array.isArray(data)) setSpeakers(data);
        } catch (e) { /* ignore */ }
    }, [sessionDeviceId]);

    useEffect(() => {
        if (!sessionDeviceId || !tokenValid) return;
        fetchTranscripts();
        fetchSpeakers();
        // Light REST polling as a belt-and-suspenders backup to SocketIO;
        // SocketIO gives us live updates, this catches anything that slipped.
        const interval = setInterval(() => {
            fetchTranscripts();
            fetchSpeakers();
        }, 5000);
        return () => clearInterval(interval);
    }, [sessionDeviceId, tokenValid, fetchTranscripts, fetchSpeakers]);

    // ---------- SocketIO: group room + event handlers ----------
    useEffect(() => {
        if (!sessionDeviceId || !tokenValid) return;

        const socket = io();
        socketRef.current = socket;

        socket.on('connect', () => {
            socket.emit('join_group_room', { session_device_id: sessionDeviceId });
            // Also join the facilitation room so the panel below receives messages.
            socket.emit('join_facilitation', { session_device_id: sessionDeviceId });
        });

        socket.on('group_viewer_count', (payload) => {
            try {
                const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
                if (data.session_device_id === sessionDeviceId) {
                    setViewerCount(data.count || 1);
                }
            } catch (e) {}
        });

        socket.on('speaker_renamed', (payload) => {
            try {
                const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
                if (data.session_device_id !== sessionDeviceId) return;
                setSpeakers(prev =>
                    prev.map(s => s.id === data.speaker_id ? { ...s, alias: data.alias } : s)
                );
                setTranscripts(prev =>
                    prev.map(t => t.speaker_id === data.speaker_id ? { ...t, speaker_tag: data.alias } : t)
                );
            } catch (e) {}
        });

        socket.on('group_ended', (payload) => {
            try {
                const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
                if (data.session_device_id !== sessionDeviceId) return;
                // Only transition to EDIT if we're still in LIVE.
                // group_ended can arrive AFTER session_finalized if the
                // TA ends the whole session after a recorder already
                // finalized — we must not downgrade the finalized view
                // back to edit mode.
                setPhase(prev => prev === PHASE_LIVE ? PHASE_EDIT : prev);
                fetchTranscripts();
                fetchSpeakers();
            } catch (e) {}
        });

        socket.on('session_finalized', (payload) => {
            try {
                const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
                if (data.session_device_id === sessionDeviceId) {
                    setPhase(PHASE_FINALIZED);
                }
            } catch (e) {}
        });

        return () => {
            try {
                socket.emit('leave_group_room', { session_device_id: sessionDeviceId });
                socket.disconnect();
            } catch (e) {}
        };
    }, [sessionDeviceId, tokenValid, fetchTranscripts, fetchSpeakers]);

    // ---------- Speaker rename ----------
    const startEdit = (speaker) => {
        if (speaker.alias === 'Blinc') return;
        setEditingSpeakerId(speaker.id);
        setEditValue(speaker.alias || '');
        setRenameError(null);
    };

    const commitEdit = async () => {
        const alias = editValue.trim();
        if (!alias || !editingSpeakerId) {
            setEditingSpeakerId(null);
            return;
        }
        try {
            const res = await scopedFetch(
                `/api/v1/devices/${sessionDeviceId}/speakers/${editingSpeakerId}/rename`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ alias }),
                }
            );
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                setRenameError(body.message || `Failed to rename (HTTP ${res.status})`);
                return;
            }
            // Optimistic update — the SocketIO broadcast will also arrive and no-op.
            setSpeakers(prev => prev.map(s => s.id === editingSpeakerId ? { ...s, alias } : s));
            setTranscripts(prev => prev.map(t => t.speaker_id === editingSpeakerId ? { ...t, speaker_tag: alias } : t));
        } finally {
            setEditingSpeakerId(null);
            setEditValue('');
        }
    };

    // ---------- Render ----------
    if (!tokenValid) {
        return (
            <div style={{ padding: 40, textAlign: 'center' }}>
                <h2>Session unavailable</h2>
                <p>Your access has expired or the group was closed.</p>
                <button onClick={() => navigate('/viewer')}
                    style={{ padding: '10px 20px', marginTop: 16 }}>
                    Join again
                </button>
            </div>
        );
    }

    return (
        // NOTE on sizing: #root in index.css is `display: flex` with row
        // direction and `overflow-clip`. A child without width/flex would
        // collapse to min-content and appear blank. We must explicitly
        // take the full width of the parent flex row.
        <div style={{
            flex: 1, width: '100%', minWidth: 0, minHeight: '100vh',
            background: '#f9fafb', display: 'flex', flexDirection: 'column',
        }}>
            <header style={{
                background: '#fff', padding: '14px 20px',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
                <div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Group</div>
                    <div style={{ fontSize: '1.05rem', fontWeight: 600 }}>
                        {recorderName ? `${recorderName}'s group` : `Group ${sessionDeviceId}`}
                    </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <div style={{
                        fontSize: '0.7rem', fontWeight: 600, padding: '3px 8px',
                        borderRadius: 12, display: 'inline-block',
                        background: phase === PHASE_LIVE ? '#dcfce7' : phase === PHASE_EDIT ? '#fef3c7' : '#dbeafe',
                        color: phase === PHASE_LIVE ? '#166534' : phase === PHASE_EDIT ? '#92400e' : '#1e40af',
                    }}>
                        {phase === PHASE_LIVE && '● LIVE'}
                        {phase === PHASE_EDIT && '✎ EDIT SPEAKERS'}
                        {phase === PHASE_FINALIZED && '✓ FINALIZED'}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: 4 }}>
                        {viewerCount} connected
                    </div>
                </div>
            </header>

            {phase === PHASE_FINALIZED ? (
                <ArtifactsView
                    sessionDeviceId={sessionDeviceId}
                    sessionId={parseInt(sessionStorage.getItem('byod_viewer_session_id') || '0', 10)}
                />
            ) : (
                <div style={{
                    flex: 1, display: 'grid',
                    gridTemplateColumns: 'minmax(0, 2fr) minmax(300px, 1fr)',
                    gap: 16, padding: 16, minHeight: 0,
                }}>
                    {/* Transcript pane */}
                    <div style={{
                        background: '#fff', borderRadius: 8,
                        border: '1px solid #e5e7eb', padding: 16,
                        overflow: 'auto', minHeight: 0,
                    }}>
                        {phase === PHASE_EDIT && (
                            <div style={{
                                marginBottom: 12, padding: '10px 12px',
                                background: '#fef3c7', border: '1px solid #fcd34d',
                                borderRadius: 6, fontSize: '0.85rem', color: '#92400e',
                            }}>
                                The recording has ended. Review the transcript and fix any speaker
                                names below. The recorder will finalize when your group is ready.
                            </div>
                        )}
                        <h3 style={{ margin: '0 0 12px', fontSize: '0.9rem', color: '#374151' }}>
                            Live transcript
                        </h3>
                        {transcripts.length === 0 ? (
                            <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                                Waiting for the discussion to start…
                            </div>
                        ) : (
                            <div style={{ fontSize: '0.88rem', lineHeight: 1.5 }}>
                                {transcripts.map((t, i) => (
                                    <div key={t.id || i} style={{ marginBottom: 8 }}>
                                        <span style={{ fontWeight: 600, color: '#4b5563' }}>
                                            {t.speaker_tag || 'Unknown'}:
                                        </span>{' '}
                                        <span style={{ color: '#1f2937' }}>{t.transcript}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Right column: speakers + facilitation */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minHeight: 0 }}>
                        <div style={{
                            background: '#fff', borderRadius: 8,
                            border: '1px solid #e5e7eb', padding: 16,
                        }}>
                            <h3 style={{ margin: '0 0 12px', fontSize: '0.9rem', color: '#374151' }}>
                                Speakers {phase === PHASE_EDIT && <span style={{ color: '#6b7280', fontWeight: 400 }}>(click to rename)</span>}
                            </h3>
                            {speakers.filter(s => s.alias !== 'Blinc').length === 0 ? (
                                <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>No speakers yet.</div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {speakers.filter(s => s.alias !== 'Blinc').map(s => (
                                        <div key={s.id}>
                                            {editingSpeakerId === s.id ? (
                                                <div style={{ display: 'flex', gap: 6 }}>
                                                    <input
                                                        autoFocus
                                                        value={editValue}
                                                        onChange={(e) => setEditValue(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') commitEdit();
                                                            if (e.key === 'Escape') setEditingSpeakerId(null);
                                                        }}
                                                        style={{
                                                            flex: 1, padding: '6px 10px',
                                                            border: '1px solid #2563eb', borderRadius: 4,
                                                        }}
                                                    />
                                                    <button onClick={commitEdit} style={{ padding: '6px 10px' }}>
                                                        Save
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => phase === PHASE_EDIT && startEdit(s)}
                                                    disabled={phase !== PHASE_EDIT}
                                                    style={{
                                                        width: '100%', textAlign: 'left',
                                                        padding: '8px 12px', background: '#f9fafb',
                                                        border: '1px solid #e5e7eb', borderRadius: 4,
                                                        fontSize: '0.88rem',
                                                        cursor: phase === PHASE_EDIT ? 'pointer' : 'default',
                                                    }}
                                                >
                                                    {s.alias || `Speaker ${s.id}`}
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {renameError && (
                                <div style={{ marginTop: 8, fontSize: '0.8rem', color: '#b91c1c' }}>
                                    {renameError}
                                </div>
                            )}
                            {phase === PHASE_EDIT && (
                                <div style={{
                                    marginTop: 12, fontSize: '0.75rem', color: '#6b7280',
                                    fontStyle: 'italic',
                                }}>
                                    Waiting for the recorder to finalize…
                                </div>
                            )}
                        </div>

                        {/* Rolling feeds: Pulse always, Blinc stacked below it. */}
                        <div style={{
                            flex: 1, display: 'flex', flexDirection: 'column',
                            gap: 10, minHeight: 0,
                        }}>
                            <div style={{ flex: '0 0 45%', minHeight: 0, overflow: 'hidden' }}>
                                <DiscussionPulsePanel
                                    sessionDeviceId={sessionDeviceId}
                                    isSessionActive={phase === PHASE_LIVE}
                                    fill={true}
                                />
                            </div>
                            <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                                <FacilitationPanel
                                    sessionDeviceId={sessionDeviceId}
                                    socket={socketRef.current}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ---------- Artifacts phase (finalized) ----------
// Layout mirrors the live recording view:
//   - Left column (wider): assessment + concept map stacked
//   - Right column (narrower, fixed-ish width): Discussion Pulse only
// Transcript is hidden behind a "Transcript" button that opens a modal
// overlay, matching how the User Study dashboard surfaces it. Click the
// backdrop or press Escape to close.
function ArtifactsView({ sessionDeviceId, sessionId }) {
    const [transcriptOpen, setTranscriptOpen] = useState(false);

    // Escape closes the modal
    useEffect(() => {
        if (!transcriptOpen) return;
        const onKey = (e) => { if (e.key === 'Escape') setTranscriptOpen(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [transcriptOpen]);

    return (
        <div style={{ padding: 16, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 14px', background: '#dbeafe',
                borderRadius: 6, marginBottom: 12, fontSize: '0.85rem', color: '#1e40af',
            }}>
                <span>The discussion is finalized. Explore the assessment and concept map below.</span>
                <button
                    onClick={() => setTranscriptOpen(true)}
                    style={{
                        padding: '6px 14px', background: '#fff', border: '1px solid #93c5fd',
                        borderRadius: 6, color: '#1e40af', fontSize: '0.82rem', fontWeight: 600,
                        cursor: 'pointer',
                    }}
                >
                    📄 Transcript
                </button>
            </div>

            <div style={{
                flex: 1, display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) minmax(280px, 360px)',
                gap: 16, minHeight: 0,
            }}>
                {/* Left: assessment + concept map */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minHeight: 0, overflow: 'auto' }}>
                    <div style={{
                        background: '#fff', borderRadius: 8,
                        border: '1px solid #e5e7eb', padding: 16,
                    }}>
                        <h3 style={{ margin: '0 0 12px', fontSize: '1rem' }}>Collaboration assessment</h3>
                        <SevenCsPanel sessionDeviceId={sessionDeviceId} readOnly={true} />
                    </div>
                    <div style={{
                        background: '#fff', borderRadius: 8,
                        border: '1px solid #e5e7eb', padding: 16, minHeight: 500,
                    }}>
                        <h3 style={{ margin: '0 0 12px', fontSize: '1rem' }}>Concept map</h3>
                        <ConceptMapView sessionDeviceId={sessionDeviceId} readOnly={true} />
                    </div>
                </div>

                {/* Right: Pulse only (no Blinc post-finalize) */}
                <div style={{ minHeight: 0, overflow: 'hidden' }}>
                    <DiscussionPulsePanel
                        sessionDeviceId={sessionDeviceId}
                        isSessionActive={false}
                        fill={true}
                    />
                </div>
            </div>

            {transcriptOpen && (
                <TranscriptModal
                    sessionDeviceId={sessionDeviceId}
                    sessionId={sessionId}
                    onClose={() => setTranscriptOpen(false)}
                />
            )}
        </div>
    );
}

// Modal overlay containing the read-only TranscriptPanel. Backdrop click
// closes; the panel itself stops propagation so clicks inside don't dismiss.
function TranscriptModal({ sessionDeviceId, sessionId, onClose }) {
    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(15, 23, 42, 0.55)', zIndex: 9999,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 24,
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: '100%', maxWidth: 760, height: '85vh',
                    background: '#fff', borderRadius: 10,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
                    display: 'flex', flexDirection: 'column', overflow: 'hidden',
                }}
            >
                <TranscriptPanel
                    deviceId={sessionDeviceId}
                    sessionId={sessionId}
                    speakerVersion={0}
                    readOnly={true}
                    onClose={onClose}
                />
            </div>
        </div>
    );
}
